import styled from '@emotion/styled';
import {
  Button,
  Grid,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import { COLOR_TEXT_MAIN } from '../../theme/colors';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import LoadingButton from '../component/primitive/LoadingButton';
import StyledInputField from '../component/primitive/StyledInputField';
import { Organization } from '@kaizenplatform/intelligence-entity';
import Loading from '../component/compound/Loading';
import RootDomain from '../../entity/RootDomain';
import SiteScript from '../../entity/SiteScript';
import WarningIcon from '../component/primitive/Icons/WarningIcon';
import { PageContainer, PageHeader, PageTitle, PaperButton, PaperWarningButton } from '../component/primitive/Page';
import {
  PaperContent,
  PaperDivider,
  PaperFooter,
  PaperItem,
  PaperText,
  PaperTitle,
  PaperTitleText,
  StyledPaper,
} from '../component/primitive/Paper';
import {
  ModalButton,
  ModalCancelButton,
  ModalContent,
  ModalFooter,
  ModalText,
  ModalTitle,
  StyledModal,
} from '../component/primitive/Modal';
import {
  DrawerButton,
  DrawerCancelButton,
  DrawerContainer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  StyledDrawer,
} from '../component/primitive/Drawer';

type Props = {
  organization: Organization | null;
  notifyUnimportant: (
    message: string,
    { messageValues, action, actionValues, isRaw, onClickAction, severity }?: any,
  ) => void;
  siteScript: SiteScript | null;
  siteScriptLoading: boolean;
  rootDomains: RootDomain[] | null;
  rootDomainsLoading: boolean;
  siteJsPermission: boolean | null;
  siteJsPermissionLoading: boolean;
  postRootDomain: (name: string) => Promise<void>;
  putRootDomain: (name: string, rootDomain: RootDomain) => Promise<void>;
  deleteRootDomain: (rootDomain: RootDomain) => Promise<void>;
  putSiteScript: (isEmpty: boolean) => Promise<void>;
  onMount: () => void;
  onUnmount?: () => void;
  className?: string;
};

const JavascriptCodePage: FC<Props> = ({
  organization,
  notifyUnimportant,
  siteScript,
  siteScriptLoading,
  rootDomains,
  rootDomainsLoading,
  siteJsPermission,
  siteJsPermissionLoading,
  postRootDomain,
  putRootDomain,
  deleteRootDomain,
  putSiteScript,
  onMount,
  onUnmount,
  className,
}) => {
  useEffect(() => {
    if (organization) {
      onMount();
    }

    return () => {
      if (onUnmount) onUnmount();
    };
  }, [organization]);

  const [, copyToClipBoard] = useCopyToClipboard();
  const [openPostDrawer, setOpenPostDrawer] = useState(false);
  const [openPutDrawer, setOpenPutDrawer] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<RootDomain>();
  const [domain, setDomain] = useState('');
  const [formatError, setFormatError] = useState(false);
  const regex = new RegExp(/[^.]+\.[^.]+/);

  useEffect(() => {
    if (domain.length > 0) {
      setFormatError(!regex.test(domain));
    }
  }, [domain]);

  const handleLinkCopy = (e: React.MouseEvent<unknown>, sync = false) => {
    e.stopPropagation();

    if (siteScript) {
      copyToClipBoard(sync ? siteScript.embedCodeSync : siteScript.embedCode)
        .then((result) => {
          if (result) {
            notifyUnimportant('コードをコピーしました', { severity: 'success' });
          } else {
            notifyUnimportant('コードのコピーに失敗しました', { severity: 'error' });
          }
        })
        .catch(() => {
          notifyUnimportant('コードのコピーに失敗しました', { severity: 'error' });
        });
    }
  };

  const togglePostDrawer = (openPostDrawer: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setOpenPostDrawer(openPostDrawer);
  };

  const togglePutDrawer = (openPutDrawer: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setOpenPutDrawer(openPutDrawer);
  };

  const toggleDeleteModal = (openModal: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setOpenModal(openModal);
  };

  const closePostDrawer = () => {
    setDomain('');
    setOpenPostDrawer(false);
  };

  const closePutDrawer = () => {
    setDomain('');
    setSelectedDomain(undefined);
    setOpenPutDrawer(false);
  };

  const closeDeleteModal = () => {
    setSelectedDomain(undefined);
    setOpenModal(false);
  };

  const handleDomainChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDomain(event.target.value);
  };

  const onClickPostDomainButton = async () => {
    try {
      await postRootDomain(domain);
      closePostDrawer();
      notifyUnimportant('ドメインの追加に成功しました。');
    } catch (error) {
      notifyUnimportant('ドメインの追加に失敗しました。', { severity: 'error' });
    }
  };

  const onClickPutDomainButton = async () => {
    try {
      if (!selectedDomain) {
        return;
      }

      await putRootDomain(domain, selectedDomain);
      closePutDrawer();
      notifyUnimportant('ドメインの編集に成功しました。');
    } catch (error) {
      notifyUnimportant('ドメインの編集に失敗しました。', { severity: 'error' });
    }
  };

  const onClickDeleteDomainButton = async () => {
    try {
      if (!selectedDomain) {
        return;
      }

      await deleteRootDomain(selectedDomain);
      notifyUnimportant('ドメインの削除に成功しました。');
    } catch (error) {
      notifyUnimportant('ドメインの削除に失敗しました。', { severity: 'error' });
    } finally {
      closeDeleteModal();
    }
  };

  const onClickEmergencyStopButton = async () => {
    try {
      if (!siteScript) {
        return;
      }

      await putSiteScript(!siteScript.isEmpty);
      notifyUnimportant('緊急停止の実施に成功しました。');
    } catch (error) {
      console.log(error);
      notifyUnimportant('緊急停止の実施に失敗しました。', { severity: 'error' });
    }
  };

  const onClickPutDomain = (rootDomain: RootDomain) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setDomain(rootDomain.name);
    setSelectedDomain(rootDomain);
    setOpenPutDrawer(true);
  };

  const onClickDeleteDomain = (rootDomain: RootDomain) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setSelectedDomain(rootDomain);
    setOpenModal(true);
  };

  return (
    <div className={className}>
      <StyledModal
        open={openModal}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            toggleDeleteModal(false);
          }
        }}
      >
        <ModalContent>
          <ModalTitle>ドメインを削除しようとしています</ModalTitle>
          <ModalText>
            {'ドメインを削除すると正常にエクスペリエンスが実行されないおそれがあります。\n削除しますか？'}
          </ModalText>
        </ModalContent>
        <ModalFooter>
          <ModalCancelButton onClick={closeDeleteModal}>キャンセル</ModalCancelButton>
          <ModalButton onClick={onClickDeleteDomainButton} loading={rootDomainsLoading}>
            OK
          </ModalButton>
        </ModalFooter>
      </StyledModal>
      <StyledDrawer
        anchor="right"
        open={openPostDrawer}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            togglePostDrawer(false);
          }
        }}
      >
        <DrawerContainer>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>Cookieをサブドメイン単位で発行</DrawerTitle>
            </DrawerHeader>
            <Content>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <StyledInputField
                    placeholder="example.com"
                    value={domain}
                    text="ドメイン"
                    onChange={handleDomainChange}
                    error={formatError}
                  />
                  {formatError && <InputError>無効な値です</InputError>}
                </Grid>
              </Grid>
            </Content>
          </DrawerContent>
          <DrawerFooter>
            <DrawerCancelButton onClick={closePostDrawer}>キャンセル</DrawerCancelButton>
            <DrawerButton
              loading={rootDomainsLoading}
              disabled={!domain || formatError}
              onClick={onClickPostDomainButton}
            >
              保存する
            </DrawerButton>
          </DrawerFooter>
        </DrawerContainer>
      </StyledDrawer>
      <StyledDrawer
        anchor="right"
        open={openPutDrawer}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            togglePutDrawer(false);
          }
        }}
      >
        <DrawerContainer>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>ドメインを編集</DrawerTitle>
            </DrawerHeader>
            <Content>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <StyledInputField
                    placeholder="example.com"
                    value={domain}
                    text="ドメイン"
                    onChange={handleDomainChange}
                    error={formatError}
                  />
                  {formatError && <InputError>無効な値です</InputError>}
                </Grid>
              </Grid>
            </Content>
          </DrawerContent>
          <DrawerFooter>
            <DrawerCancelButton onClick={closePutDrawer}>キャンセル</DrawerCancelButton>
            <DrawerButton
              loading={rootDomainsLoading}
              disabled={!domain || formatError}
              onClick={onClickPutDomainButton}
            >
              保存する
            </DrawerButton>
          </DrawerFooter>
        </DrawerContainer>
      </StyledDrawer>
      <PageContainer>
        <PageHeader>
          <PageTitle>JavaScriptコードの設定</PageTitle>
        </PageHeader>
        {siteJsPermissionLoading ? (
          <LoadingContainer>
            <Loading />
          </LoadingContainer>
        ) : (
          <Fragment>
            {siteJsPermission ? (
              <StyledPaper>
                <PaperTitle>
                  <PaperTitleText>JavaScriptコードの設定手順</PaperTitleText>
                </PaperTitle>
                <PaperContent>
                  {siteScript?.isEmpty ? (
                    <PaperItem>
                      <WarningGrid item xs={12}>
                        <WarningIconContainer>
                          <WarningIcon />
                        </WarningIconContainer>
                        <PaperText>
                          {
                            '緊急停止ボタンにより全てのA/Bテストが停止されています。再開するには、 JavaScriptファイルを再構築するボタンを押してください。'
                          }
                        </PaperText>
                      </WarningGrid>
                    </PaperItem>
                  ) : null}
                  <PaperItem>
                    <PaperText>
                      {
                        '次のいずれかのコードをコピーして、デザイン変更・ログ計測の対象とするすべてのページに貼り付けてください。貼り付け位置は </head> の直前です。'
                      }
                    </PaperText>
                    <WarningText>
                      ※ JavaScriptコードを設定するにあたり、Google Tag
                      Manager等のツールを用いてしまうと、今後iOSのセキュリティによりうまく動作しなくなる可能性がございますため、直接サイトに貼り付けることを推奨します。詳細な情報は
                      <Link href="https://support.kaizenplatform.net/hc/ja/articles/52295217442713" target="_blank">
                        こちら
                      </Link>
                    </WarningText>
                  </PaperItem>
                  <InnerDivider />
                  <PaperItem>
                    <NoticeText>
                      {'async(非同期)版とsync(同期)版の使い分けについては'}
                      <Link href="https://support.kaizenplatform.net/hc/ja/articles/52295217442713" target="_blank">
                        こちら
                      </Link>
                      {'をご確認ください。'}
                      <WarningText>
                        ※
                        JavaScriptコードを同一ページに複数配置すると、予期しない動作や不具合が発生する恐れがあります。必ず1ページにつき1つのコードのみを設定してください。
                      </WarningText>
                    </NoticeText>
                    <InnerHeading>async(非同期)版</InnerHeading>
                    <PaperText>
                      {
                        'ページ表示をブロックせずに読み込むため表示の体感が軽くなりますが、デザイン変更・ログ計測が若干遅延する可能性があります。ページ表示の体感を優先する場合やGoogle Tag Managerを利用している場合はこちらをお使いください。'
                      }
                    </PaperText>
                  </PaperItem>
                  <PaperItem>
                    {siteScriptLoading || !siteScript ? (
                      <Loading />
                    ) : (
                      <ScriptContainer>
                        {siteScript.embedCode}
                        <Tooltip title="コードをコピー">
                          <CopyButton
                            onClick={(e) => {
                              handleLinkCopy(e);
                            }}
                          >
                            <FileCopyIcon />
                          </CopyButton>
                        </Tooltip>
                      </ScriptContainer>
                    )}
                  </PaperItem>
                  <InnerDivider />
                  <PaperItem>
                    <InnerHeading>sync(同期)版</InnerHeading>
                    <PaperText>
                      {
                        'ページ表示をブロックして確実に読み込むため、デザイン変更・ログ計測の遅延は低くなりますが、表示の体感が重くなる可能性があります。'
                      }
                    </PaperText>
                  </PaperItem>
                  <PaperItem>
                    {siteScriptLoading || !siteScript ? (
                      <Loading />
                    ) : (
                      <ScriptContainer>
                        {siteScript.embedCodeSync}
                        <Tooltip title="コードをコピー">
                          <CopyButton
                            onClick={(e) => {
                              handleLinkCopy(e, true);
                            }}
                          >
                            <FileCopyIcon />
                          </CopyButton>
                        </Tooltip>
                      </ScriptContainer>
                    )}
                  </PaperItem>
                </PaperContent>
              </StyledPaper>
            ) : null}
            {siteJsPermission ? (
              <StyledPaper>
                <PaperTitle>
                  <PaperTitleText>Cookieドメインの管理</PaperTitleText>
                </PaperTitle>
                <PaperContent>
                  <PaperItem>
                    <PaperText>
                      {
                        'Kaizen Platform では デザインを改善するページのURL や ゴール設定 から、ルートドメイン ( example.net など) を自動で判別し、訪問者をトラッキングするための Cookie をルートドメインに発行します。'
                      }
                    </PaperText>
                  </PaperItem>
                  <PaperItem>
                    <PaperText>
                      {
                        'そのため、サブドメイン ( abc.example.net など) を跨いでユーザーをトラッキングし、エクスペリエンスの効果測定を行うことができます。'
                      }
                    </PaperText>
                  </PaperItem>
                  <PaperItem>
                    <PaperText>
                      {
                        'ルートドメインへの Cookie 書き込みが制限されているなど、閲覧中のページのドメインに Cookie を発行する必要がある場合は右上の「ドメインを追加する」ボタンから対象となるルートドメインを登録してください。'
                      }
                    </PaperText>
                  </PaperItem>
                  {rootDomains && rootDomains.length > 0 ? (
                    <PaperItem>
                      <Table>
                        <TableHead>
                          <StyledTableRow>
                            <StyledTableHeaderCell>Cookie をサブドメイン単位で発行</StyledTableHeaderCell>
                            <StyledTableHeaderCell align="right" />
                          </StyledTableRow>
                        </TableHead>
                        <TableBody>
                          {rootDomains.map((rootDomain: RootDomain) => (
                            <TableRow key={rootDomain.id}>
                              <StyledTableCell>{rootDomain.name}</StyledTableCell>
                              <StyledTableCell align="right">
                                <SmallButton onClick={onClickPutDomain(rootDomain)}>編集</SmallButton>
                                <DomainDeleteButton onClick={onClickDeleteDomain(rootDomain)}>削除</DomainDeleteButton>
                              </StyledTableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </PaperItem>
                  ) : null}
                  <PaperDivider />
                  <PaperFooter>
                    <PaperButton onClick={togglePostDrawer(true)}>ドメインを追加する</PaperButton>
                  </PaperFooter>
                </PaperContent>
              </StyledPaper>
            ) : null}
            {siteJsPermission ? (
              <StyledPaper>
                <PaperTitle>
                  <PaperTitleText>緊急停止</PaperTitleText>
                </PaperTitle>
                <PaperContent>
                  <PaperItem>
                    <PaperText>
                      {
                        '万が一JavaScriptコードからのデータ送信を停止する必要が発生した場合は、下のボタンを押してください。JavaScriptファイルの中身が空になります。'
                      }
                    </PaperText>
                  </PaperItem>
                  <PaperDivider />
                  <PaperFooter>
                    <PaperWarningButton onClick={onClickEmergencyStopButton}>
                      {siteScript?.isEmpty ? 'JavaScriptファイルを再構築する' : '緊急停止する'}
                    </PaperWarningButton>
                  </PaperFooter>
                </PaperContent>
              </StyledPaper>
            ) : null}
            {!siteJsPermission ? (
              <StyledPaper>
                <PaperTitle>
                  <PaperTitleText>請求書決済のお客様</PaperTitleText>
                </PaperTitle>
                <PaperContent>
                  <PaperItem>
                    <PaperText>{`ご契約情報が登録されておりません。サポートまでお問い合わせください。`}</PaperText>
                  </PaperItem>
                </PaperContent>
              </StyledPaper>
            ) : null}
          </Fragment>
        )}
      </PageContainer>
    </div>
  );
};

const Content = styled('div')(`
  height: 100%;
  width: 100%;
`);

const LoadingContainer = styled('div')(`
  width: 100%;
  padding-top: 24px;
`);

const ScriptContainer = styled('div')(`
  background: #4C566C;
  border-radius: 8px;
  color: #2DD28D;
  padding: 24px;
  white-space: pre-wrap;
  user-select: all;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 14px;
  line-height: 160%;
  font-weight: 500;
`);

const CopyButton = styled(IconButton)(`
  color: #FFFFFF;
  margin-top: -12px;
  margin-right: -12px;
`);

const SmallButton = styled(LoadingButton)(`
  color: #FFFFFF;
  height: 32px;
  font-size: 12px;
  background: ${COLOR_TEXT_MAIN};
  font-weight: 800;
  border-radius: 4px;
  padding: 10px 12px;
  :hover {
    background: #707889;
    border-color: #707889;
  }
  :disabled {
    background: #E6E7E8;
    color: rgba(75, 85, 106, 0.24);
    border: rgba(75, 85, 106, 0.24);
  }
`);

const DomainDeleteButton = styled(Button)(`
  color: ${COLOR_TEXT_MAIN};
  height: 32px;
  font-size: 12px;
  background: #FFFFFF;
  font-weight: 800;
  border: 1.5px solid #E6E7E8;
  border-radius: 4px;
  padding: 10px 12px;
  margin-left: 12px;
  :hover {
    background: #FFFFFF;
  }
`);

const StyledTableRow = styled(TableRow)(`
  background: #FFFFFF;
`);

const StyledTableHeaderCell = styled(TableCell)(`
  font-weight: 700;
  font-size: 12px;
  color: ${COLOR_TEXT_MAIN};
`);

const StyledTableCell = styled(TableCell)(`
  font-size: 12px;
  color: ${COLOR_TEXT_MAIN};
`);

const InputError = styled(Typography)(`
  color: #FE7877;
`);

const WarningGrid = styled(Grid)(`
  display: flex;
  align-items: center;
`);

const WarningIconContainer = styled('div')(`
  display: flex;
  margin-right: 4px;
`);

const InnerHeading = styled('div')(`
  font-family: Hiragino Sans;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  color: ${COLOR_TEXT_MAIN};
  margin-bottom: 16px;
`);

const InnerDivider = styled(PaperDivider)(`
  margin-bottom: 0;
`);

const WarningText = styled(PaperText)(`
  color: #FE7877;
  margin-top: 8px;
`);

const NoticeText = styled(PaperText)(`
  margin-top: 0px;
  margin-bottom: 24px;
`);

export default JavascriptCodePage;
